import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useAuth } from '../auth/useAuth';
import { AppLink } from '../components/AppLink';
import { BrandMark } from '../components/BrandMark';
import {
  getPendingCaptureCount,
  getTodayLocalCaptureCount,
  saveLocalCapture,
} from '../lib/local-db';
import { resumePendingAnalysis, syncPendingCaptures } from '../lib/sync';

const DRAFT_KEY = 'ai-inbox-capture-draft';

function currentDateLabel() {
  return new Intl.DateTimeFormat('zh-CN', {
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  }).format(new Date());
}

export function CapturePage() {
  const { client, signOut, user } = useAuth();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const savingLock = useRef(false);
  const feedbackTimer = useRef<number | null>(null);
  const [text, setText] = useState(() => localStorage.getItem(DRAFT_KEY) || '');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (text) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  }, [text]);

  useEffect(() => {
    let active = true;

    async function refreshCounts() {
      const [pending, localToday] = await Promise.all([
        getPendingCaptureCount(user!.id),
        getTodayLocalCaptureCount(user!.id),
      ]);
      if (active) {
        setPendingCount(pending);
        setTodayCount(localToday);
      }
    }

    async function syncNow() {
      if (!client || !navigator.onLine) return;
      setSyncing(true);
      await syncPendingCaptures(client);
      await resumePendingAnalysis(client);
      await refreshCounts();
      if (active) setSyncing(false);
    }

    const handleOnline = () => {
      setOnline(true);
      void syncNow();
    };
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    void refreshCounts();
    void syncNow();

    return () => {
      active = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [client, user]);

  async function syncNow() {
    if (!client || !navigator.onLine || syncing) return;
    setSyncing(true);
    try {
      await syncPendingCaptures(client);
      await resumePendingAnalysis(client);
      setPendingCount(await getPendingCaptureCount(user!.id));
    } finally {
      setSyncing(false);
    }
  }

  async function save() {
    const rawText = text.trim();
    if (!rawText || savingLock.current) return;

    savingLock.current = true;
    setSaving(true);
    try {
      await saveLocalCapture(rawText, user!.id);
      setText('');
      setTodayCount((count) => count + 1);
      setPendingCount((count) => count + 1);
      setFeedback(online ? '已安全保存，正在整理' : '已离线保存，联网后自动同步');
      if (feedbackTimer.current) window.clearTimeout(feedbackTimer.current);
      feedbackTimer.current = window.setTimeout(() => setFeedback(''), 2600);
      navigator.vibrate?.(20);
      void syncNow();
    } catch {
      setFeedback('本地保存失败，内容仍保留在输入框，请重试');
    } finally {
      savingLock.current = false;
      setSaving(false);
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void save();
    }
  }

  return (
    <main className="capture-page">
      <header className="capture-header">
        <div className="brand-row">
          <BrandMark />
          <span>念头收件箱</span>
        </div>
        <nav>
          <AppLink href="/app">新工作区</AppLink>
          <AppLink href="/inbox">查看收件箱</AppLink>
          <button className="text-button" onClick={() => void signOut()}>
            退出
          </button>
        </nav>
      </header>

      <section className="capture-stage">
        <div className="capture-meta">
          <p>{currentDateLabel()}</p>
          <span className={online ? 'network online' : 'network'}>
            <i /> {online ? '在线' : '离线可记录'}
          </span>
        </div>
        <h1>现在想到了什么？</h1>
        <p className="capture-hint">直接说，直接写。不用标题，不用分类。</p>

        <div className="capture-composer">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={10000}
            rows={7}
            placeholder="例如：明天下午问老张样品什么时候寄…"
            aria-label="念头内容"
          />
          <div className="composer-footer">
            <span>{text.length ? `${text.length} 字` : '可以使用输入法语音按钮'}</span>
            <button className="capture-button" onClick={() => void save()} disabled={!text.trim() || saving}>
              {saving ? '保存中' : '收进来'}
              <b aria-hidden="true">↗</b>
            </button>
          </div>
        </div>

        <div className="capture-status" aria-live="polite">
          <div>
            <strong>{todayCount}</strong>
            <span>本机今日记录</span>
          </div>
          <div>
            <strong>{syncing ? '…' : pendingCount}</strong>
            <span>{syncing ? '正在同步' : '等待同步'}</span>
          </div>
          <button onClick={() => void syncNow()} disabled={!online || syncing || pendingCount === 0}>
            手动重试
          </button>
        </div>
        {feedback ? <p className="save-feedback">{feedback}</p> : null}
      </section>

      <footer className="capture-footer">
        <span>Ctrl + Enter 保存</span>
        <span>原始内容永久保留</span>
      </footer>
    </main>
  );
}
